import * as React from 'react';
import { apiFetch } from '../../../lib/apiClient';

export type PlatformLifecycleState =
  | 'no_integration_concept'
  | 'detected_no_workspace_integration'
  | 'detected_workspace_integration_available'
  | 'configured';

export interface ProjectResource {
  platformKey: string;
  label: string;
  description: string | null;
  iconKey: string | null;
  category: string | null;
  externalUrl: string | null;
  detectionPaths: string[];
  confidence: number;
  lastDetectedAt: string;
  /** 'platform' = connectable system; 'technology' = informational only */
  platformType: string;
  /** Slug of the integration_definition this platform relies on, or null */
  integrationDefinitionSlug: string | null;
  /** Lifecycle state - only set when platformType === 'platform', otherwise null */
  lifecycleState: PlatformLifecycleState | null;
}

export interface PlatformCatalogEntry {
  key: string;
  label: string;
  category: string;
  iconKey: string | null;
  externalUrl: string | null;
}

async function fetchProjectResources(projectUuid: string): Promise<ProjectResource[]> {
  const data = await apiFetch<{ resources: ProjectResource[] }>(
    `/api/project/${projectUuid}/resources`
  );
  return data.resources;
}

async function fetchAvailablePlatforms(projectUuid: string): Promise<PlatformCatalogEntry[]> {
  const data = await apiFetch<{ platforms: PlatformCatalogEntry[] }>(
    `/api/project/${projectUuid}/resources/available`
  );
  return data.platforms;
}

export function useProjectResources(projectUuid: string | undefined) {
  const [resources, setResources] = React.useState<ProjectResource[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const reload = React.useCallback(() => {
    if (!projectUuid) return;
    setLoading(true);
    setError(null);
    fetchProjectResources(projectUuid)
      .then((data) => setResources(data))
      .catch((err: unknown) => setError((err as Error).message))
      .finally(() => setLoading(false));
  }, [projectUuid]);

  React.useEffect(() => {
    if (!projectUuid) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchProjectResources(projectUuid)
      .then((data) => {
        if (!cancelled) setResources(data);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError((err as Error).message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectUuid]);

  const deleteResource = React.useCallback(
    async (platformKey: string) => {
      if (!projectUuid) return;
      await apiFetch(`/api/project/${projectUuid}/resources/${encodeURIComponent(platformKey)}`, {
        method: 'DELETE',
      });
      setResources((prev) => prev.filter((r) => r.platformKey !== platformKey));
    },
    [projectUuid]
  );

  const addResource = React.useCallback(
    async (platformKey: string) => {
      if (!projectUuid) return;
      await apiFetch(`/api/project/${projectUuid}/resources`, {
        method: 'POST',
        body: JSON.stringify({ platformKey }),
      });
      // Reload to get the full resource with metadata from the join
      reload();
    },
    [projectUuid, reload]
  );

  const getAvailablePlatforms = React.useCallback(() => {
    if (!projectUuid) return Promise.resolve([] as PlatformCatalogEntry[]);
    return fetchAvailablePlatforms(projectUuid);
  }, [projectUuid]);

  return { resources, loading, error, reload, deleteResource, addResource, getAvailablePlatforms };
}
